import { useEffect, useRef, useState } from "react";
import { FaUserPlus } from "react-icons/fa";
import { IoClose as X, IoTrash } from "react-icons/io5";

import UserMultipleSearch from "@/components/shared/users-search/user-multiple-search";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils/lib";
import type { PartialAccount } from "@/utils/types";
import { getUserDisplayName } from "@/utils/usernames";

import DesoAvatar from "../deso-avatar";
import { InfoTooltip } from "../info-tooltip";

export type MultipleSelectorProps = {
  buttonClassName?: string;
  badgeTextClassName?: string;
  badgeTextWrapperClassName?: string;
  placeholder?: string;
  onSelectedUsers?: (users: PartialAccount[]) => void;
  users?: PartialAccount[];
  pillsContainerRightPadding?: number;
};

const UserMultipleSelect = ({
  buttonClassName = "",
  badgeTextClassName = "",
  badgeTextWrapperClassName = "",
  placeholder = "Search any user...",
  onSelectedUsers,
  users = [],
  pillsContainerRightPadding = 100,
}: MultipleSelectorProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const containerRef = useRef<HTMLButtonElement>(null);
  const badgeContainerRef = useRef<HTMLDivElement>(null);
  const [visibleUsers, setVisibleUsers] = useState<PartialAccount[]>([]);
  const [hiddenCount, setHiddenCount] = useState(0);

  const handleTogglePopover = () => {
    setPopoverOpen((prev) => !prev);
  };

  const handleRemove = (
    e: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>,
    publicKey?: string,
  ) => {
    if (!publicKey) {
      onSelectedUsers?.([]);
    } else {
      onSelectedUsers?.(users.filter((user) => user.publicKey !== publicKey));
    }
    e.stopPropagation();
  };

  const handleSelect = (value: PartialAccount) => {
    onSelectedUsers?.([value, ...users]);
    setPopoverOpen(false);
  };

  useEffect(() => {
    const updateVisibleUsers = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const badgeWidth = 98;

      const totalVisibleUsers = Math.floor(
        (containerWidth - pillsContainerRightPadding) / badgeWidth,
      );

      setVisibleUsers(users.slice(0, totalVisibleUsers));
      setHiddenCount(users.length - totalVisibleUsers);
    };

    updateVisibleUsers();
    window.addEventListener("resize", updateVisibleUsers);
    return () => window.removeEventListener("resize", updateVisibleUsers);
  }, [pillsContainerRightPadding, users]);

  const renderUserBadge = (user: PartialAccount) => {
    if (!user?.publicKey) return null;

    const displayName = getUserDisplayName(user);

    return (
      <Badge
        key={user.publicKey}
        className="my-0.5 flex cursor-pointer items-center px-1"
        onClick={(e) => handleRemove(e, user.publicKey)}
      >
        <DesoAvatar account={user} size={22} />
        <span className={cn("flex w-[50px]", badgeTextWrapperClassName)}>
          <span className="inline-flex w-full text-ellipsis">
            <span
              className={cn(
                "ml-1 grow truncate overflow-hidden text-xxs",
                badgeTextClassName,
              )}
            >
              {displayName}
            </span>
          </span>
        </span>

        <X className="ml-1 size-3 text-primary-foreground" />
      </Badge>
    );
  };

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          size="lg"
          onClick={handleTogglePopover}
          ref={containerRef}
          className={cn(
            `
              hover-visible:outline-none
              relative z-1 flex w-full rounded-full border border-border
              bg-input px-3 py-2 text-sm text-muted-foreground
              hover:border-foreground hover:bg-input
              focus:border-foreground focus:bg-input
              focus-visible:outline-hidden
              disabled:cursor-not-allowed disabled:opacity-50
            `,
            buttonClassName,
          )}
        >
          {users.length > 0 ? (
            <div
              className={cn(
                `
                  flex w-full cursor-pointer items-stretch justify-between
                  text-muted-foreground
                `,
                users.length < 1 && "gap-7",
              )}
            >
              <div
                className="
                  relative flex w-[calc(100%-65px)] items-stretch gap-1
                  overflow-hidden
                "
              >
                <div className="flex items-start gap-1" ref={badgeContainerRef}>
                  {visibleUsers.map((user) => renderUserBadge(user))}
                </div>
                {hiddenCount > 0 && (
                  <InfoTooltip
                    text={
                      <div className="flex flex-wrap items-center gap-1">
                        {users
                          .slice(visibleUsers.length)
                          .map((user) => renderUserBadge(user))}
                      </div>
                    }
                  >
                    <div className="ml-2 flex items-center text-xxs">
                      + {hiddenCount}
                    </div>
                  </InfoTooltip>
                )}
              </div>
              <div
                className="
                  absolute top-0 right-[10px] z-20 flex h-full items-stretch
                "
              >
                <span className="flex items-center px-2">
                  <FaUserPlus className="size-4" />
                </span>
                <span
                  className="flex items-center px-2"
                  onClick={(e) => handleRemove(e)}
                >
                  <IoTrash className="size-4" />
                </span>
              </div>
            </div>
          ) : (
            <div
              className="
                mx-auto flex w-full items-center justify-between font-sans
                font-normal text-muted
              "
            >
              {placeholder}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto border-0 p-0"
        align="start"
        onEscapeKeyDown={() => setPopoverOpen(false)}
      >
        <UserMultipleSearch
          onSelect={handleSelect}
          users={users}
          placeholder={placeholder}
        />
      </PopoverContent>
    </Popover>
  );
};

export default UserMultipleSelect;
